/**
 * Search domain — imperative helpers and shared types.
 *
 * Every search endpoint is a read (GET) and every consumer calls it
 * imperatively (debounced keystroke handlers carrying AbortSignals), so
 * the entry point exposes thin wrappers over the generated client
 * functions rather than hooks. The wrappers preserve the signatures of
 * the legacy `search_api.ts` functions so migration is a pure import
 * swap. React Query hook variants live in `hooks.ts` for consumers that
 * want caching.
 */

import type {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../models/companies'
import { checkCompanyDomainExists } from '../generated/clients/checkCompanyDomainExists'
import { listMyCompanies as listMyCompaniesClient } from '../generated/clients/listMyCompanies'
import { searchChains } from '../generated/clients/searchChains'
import { searchContactCompanies } from '../generated/clients/searchContactCompanies'
import { searchDeals as searchDealsClient } from '../generated/clients/searchDeals'
import { searchDoors } from '../generated/clients/searchDoors'
import { searchUnmatchedChains } from '../generated/clients/searchUnmatchedChains'
import { searchUnmatchedDoors as searchUnmatchedDoorsClient } from '../generated/clients/searchUnmatchedDoors'
import { searchVariants as searchVariantsClient } from '../generated/clients/searchVariants'
import type { SearchContactCompaniesQueryParams } from '../generated/types/SearchContactCompanies'
import type { SearchDealsQueryParams } from '../generated/types/SearchDeals'
import type { SearchDealsResponse } from '../generated/types/SearchDealsResponse'
import type { SearchVariantsResponse } from '../generated/types/SearchVariantsResponse'

// ── Domain entity type re-exports ────────────────────────────────────

export type { ChainSearchResult } from '../generated/types/ChainSearchResult'
export type { DealSearchResult } from '../generated/types/DealSearchResult'
export type { UnmatchedCompanySearchResult } from '../generated/types/UnmatchedCompanySearchResult'
export type { UnmatchedDoorSearchResult } from '../generated/types/UnmatchedDoorSearchResult'
export type { SyndigoProductData } from '../generated/types/SyndigoProductData'
export type { VariantSearchResult } from '../generated/types/VariantSearchResult'
export type { SearchDealsResponse } from '../generated/types/SearchDealsResponse'
export type { SearchVariantsResponse } from '../generated/types/SearchVariantsResponse'
export type { SearchTypeEnum } from '../generated/types/SearchTypeEnum'

// ── Legacy-shaped response types ─────────────────────────────────────
//
// Consumers (UnifiedSearch, MobileSearch) accumulate results from the
// chain and door endpoints into state typed with the broad legacy model
// types, so the wrappers keep returning those shapes. The generated
// per-endpoint types are exported above for new code.

export interface SearchFilterOptions {
  category_filter?: string
  type_filter?: string
  city_filter?: string
  state_filter?: string
  zipcode_filter?: string
  sort_by?: string
  limit?: number
  offset?: number
}

export interface CompanySearchResponse {
  results: ContactCompanySearchResult[]
  total: number
  search_type?: string
}

export interface DoorSearchResponse {
  results: DoorSearchResult[]
  total: number
  search_type?: string
}

// ── Imperative search functions ──────────────────────────────────────

/** Search the tenant's contact companies by name. */
export async function searchCompaniesList(
  params: SearchContactCompaniesQueryParams,
  signal?: AbortSignal
): Promise<ContactCompanySearchResult[]> {
  return searchContactCompanies(params, { signal })
}

/** Check whether a domain already exists on any company or chain. */
export async function searchCompaniesUniqueDomain(
  domain: string,
  signal?: AbortSignal
): Promise<boolean> {
  return checkCompanyDomainExists({ domain }, { signal })
}

/**
 * Search FirstBite chains by name or domain.
 *
 * The generated `ChainSearchResult` is narrower (nullable domain, ES
 * scoring fields) than the legacy model type the consumers' state is
 * typed with, so the response is cast to the legacy shape — exactly what
 * the untyped legacy implementation did implicitly.
 */
export async function searchFirstBiteCompanies(
  search: string,
  options: SearchFilterOptions = {},
  signal?: AbortSignal
): Promise<CompanySearchResponse> {
  const response = await searchChains({ q: search, ...options }, { signal })
  return response as unknown as CompanySearchResponse
}

/** List the authenticated user's custom (unmatched) companies. */
export async function listMyCompanies(
  options: SearchFilterOptions = {},
  signal?: AbortSignal
): Promise<CompanySearchResponse> {
  const response = await listMyCompaniesClient(options, { signal })
  return response as unknown as CompanySearchResponse
}

/** Search the tenant's custom (unmatched) companies. */
export async function searchUnmatchedCompanies(
  search: string,
  options: SearchFilterOptions = {},
  signal?: AbortSignal
): Promise<CompanySearchResponse> {
  const response = await searchUnmatchedChains(
    { q: search, ...options },
    { signal }
  )
  return response as unknown as CompanySearchResponse
}

/** Search FirstBite doors by name, domain or address. */
export async function searchFirstBiteDoors(
  search: string,
  options: SearchFilterOptions = {},
  signal?: AbortSignal
): Promise<DoorSearchResponse> {
  const response = await searchDoors({ q: search, ...options }, { signal })
  return response as unknown as DoorSearchResponse
}

/** Search the tenant's custom (unmatched) doors. */
export async function searchUnmatchedDoors(
  search: string,
  options: SearchFilterOptions = {},
  signal?: AbortSignal
): Promise<DoorSearchResponse> {
  const response = await searchUnmatchedDoorsClient(
    { q: search, ...options },
    { signal }
  )
  return response as unknown as DoorSearchResponse
}

/** Search the tenant's product variants. */
export async function searchVariants(
  search: string,
  options: { limit?: number; offset?: number; product_id?: number } = {},
  signal?: AbortSignal
): Promise<SearchVariantsResponse> {
  return searchVariantsClient({ q: search, ...options }, { signal })
}

/** Search the tenant's deals. */
export async function searchDeals(
  params: SearchDealsQueryParams,
  signal?: AbortSignal
): Promise<SearchDealsResponse> {
  return searchDealsClient(params, { signal })
}
